import {
  Bell,
  Building2,
  Calendar,
  CalendarClock,
  CheckCircle,
  Clock,
  FileCheck2,
  FileText,
  Gavel,
  Highlighter,
  Menu,
  PenLine,
  Scale,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Tags,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { fetchPlansPublic, type PlanoPublic } from '../api/client';
import {
  addonDisponivel,
  SecretarioAddonCard,
  useOfertaSecretario,
} from '../components/assinatura/SecretarioAddonCard';
import { useAuth } from '../hooks/useAuth';

import { PublicFooter } from './public/PublicFooter';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { formatPrice } from '@/lib/formatters';
import { LOGO_J_PATH_32 } from '@/lib/logoMark';

type NavLink = { label: string; href: string } | { label: string; to: string };

const navLinks: NavLink[] = [
  { label: 'Recursos', href: '#recursos' },
  { label: 'Demonstração', href: '#demo' },
  { label: 'Planos', href: '#planos' },
  { label: 'Sobre', href: '#sobre' },
  { label: 'Contato', to: '/contato' },
];

const features = [
  {
    icon: <Gavel className="h-7 w-7" />,
    title: 'Gestão Processual',
    description: 'Acompanhe seus processos em um único lugar, com consulta integrada aos tribunais via PDPJ.',
    variant: 'default' as const,
  },
  {
    icon: <Bell className="h-7 w-7" />,
    title: 'Comunicações & Prazos',
    description: 'Receba as comunicações do DJEN organizadas por processo, sem perder publicações importantes.',
    variant: 'primary' as const,
  },
  {
    icon: <FileText className="h-7 w-7" />,
    title: 'Documentos',
    description: 'Organize e armazene documentos com segurança, facilitando o acesso e o compartilhamento.',
    variant: 'default' as const,
  },
  {
    icon: <Send className="h-7 w-7" />,
    title: 'Peticionamento',
    description: 'Monte e envie petições com anexos e assinatura digital, com rastreabilidade de cada etapa.',
    variant: 'primary' as const,
  },
];

// Recursos disponíveis hoje na plataforma — conferir com o que está em PROD antes
// de mexer (a IA passou meses como "Em breve" depois de entrar no ar).
const additionalFeatures = [
  { icon: <Scale className="h-[22px] w-[22px]" />, title: 'Consulta Processual', desc: 'Andamentos e autos digitais integrados aos tribunais (PDPJ)' },
  { icon: <ShieldCheck className="h-[22px] w-[22px]" />, title: 'Assinatura Digital', desc: 'Assine documentos com certificado digital e validade jurídica' },
  { icon: <Users className="h-[22px] w-[22px]" />, title: 'Gestão de Clientes', desc: 'Cadastro, documentos e vínculo com processos em um só lugar' },
  { icon: <Calendar className="h-[22px] w-[22px]" />, title: 'Calendário & Audiências', desc: 'Organize compromissos com sincronização ao Google Calendar' },
  { icon: <FileText className="h-[22px] w-[22px]" />, title: 'Modelos de Documentos', desc: 'Reaproveite modelos para agilizar a produção de peças' },
  { icon: <Sparkles className="h-[22px] w-[22px]" />, title: 'Chat de IA com o Processo', desc: 'Pergunte sobre os autos e peça minutas de peças (plano Premium IA)' },
  { icon: <CalendarClock className="h-[22px] w-[22px]" />, title: 'Contagem de Prazos', desc: 'Vencimento calculado a partir da intimação, em dias úteis e com o calendário forense' },
  { icon: <FileCheck2 className="h-[22px] w-[22px]" />, title: 'Revisão de Peças com IA', desc: 'Sugestões de revisão sobre a sua minuta, aceitas uma a uma (plano Premium IA)' },
  { icon: <PenLine className="h-[22px] w-[22px]" />, title: 'Editor nos Autos', desc: 'Redija a peça ao lado dos autos, com versões salvas' },
  { icon: <Highlighter className="h-[22px] w-[22px]" />, title: 'Anotações nos Autos', desc: 'Marque trechos e guarde favoritos e notas nas peças' },
  { icon: <Tags className="h-[22px] w-[22px]" />, title: 'Tags Personalizadas', desc: 'Organize processos, clientes e documentos com cores e categorias' },
  { icon: <Building2 className="h-[22px] w-[22px]" />, title: 'Escritório e Delegação', desc: 'Dê a colegas acesso de leitura ou colaboração aos seus processos' },
];

// Roadmap — exibido como "Em breve", sem prometer como já disponível.
const roadmapFeatures = [
  { icon: <Search className="h-[22px] w-[22px]" />, title: 'Jurisprudência', desc: 'Pesquisa de decisões relevantes para seus processos' },
  { icon: <TrendingUp className="h-[22px] w-[22px]" />, title: 'Relatórios Avançados', desc: 'Métricas e insights sobre a produtividade do escritório' },
];

function LandingPageLogo() {
  return (
    <div className="flex items-center gap-3">
      <svg
        viewBox="0 0 32 32"
        className="h-10 w-10 drop-shadow-[0_2px_8px_rgba(122,36,48,0.4)]"
        aria-hidden="true"
      >
        <rect width="32" height="32" rx="5.33" fill="var(--color-primary)" />
        <path d={LOGO_J_PATH_32} fill="#ffffff" />
      </svg>
      <span className="text-base font-bold tracking-tight">
        <span className="text-primary">JURIS</span>APP
      </span>
    </div>
  );
}

function PricingSection({
  login,
}: {
  login: (planCode?: string, returnTo?: string, screenHint?: 'login' | 'signup') => void;
}) {
  const [plans, setPlans] = useState<PlanoPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [interval, setInterval] = useState<'MENSAL' | 'ANUAL'>('MENSAL');

  useEffect(() => {
    fetchPlansPublic()
      .then(setPlans)
      .finally(() => setLoading(false));
  }, []);

  const filtered = plans.filter((p) => p.intervalo === interval);
  // O add-on aparece abaixo dos planos; sem a oferta (falha de rede), só os planos.
  const { data: oferta } = useOfertaSecretario();

  return (
    <div id="planos" className="scroll-mt-20 bg-muted py-16 md:py-24">
      <div className="container mx-auto max-w-screen-lg px-4">
        <div className="mb-12 text-center">
          <h2 className="mb-4 text-3xl font-bold text-foreground">
            Escolha seu Plano
          </h2>
          <p className="mx-auto mb-8 max-w-[600px] text-base text-muted-foreground">
            Planos flexíveis para atender escritórios de todos os tamanhos
          </p>

          {/* Toggle Mensal/Anual */}
          <div className="flex items-center justify-center gap-2">
            <Button
              variant={interval === 'MENSAL' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-full"
              onClick={() => setInterval('MENSAL')}
            >
              Mensal
            </Button>
            <Button
              variant={interval === 'ANUAL' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-full"
              onClick={() => setInterval('ANUAL')}
            >
              Anual
            </Button>
            {interval === 'ANUAL' && (
              <Badge variant="secondary" className="ml-1 bg-emerald-100 text-emerald-700">
                Economize 2 meses
              </Badge>
            )}
          </div>
        </div>

        {/* Skeleton: exceção por layout shift */}
        {loading ? (
          <div className="grid grid-cols-1 justify-center gap-8 md:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="h-80 animate-pulse bg-muted-foreground/5" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-muted-foreground">
            Nenhum plano disponível no momento.
          </p>
        ) : (
          <div className="grid grid-cols-1 justify-center gap-8 md:grid-cols-3">
            {filtered.map((plan) => (
              <Card
                key={plan.id}
                className={`relative h-full transition-all ${
                  plan.destaque
                    ? 'sm:scale-105 border-none bg-primary text-white'
                    : 'border border-border bg-card'
                }`}
              >
                {plan.badge_text && (
                  <Badge
                    className={`absolute right-4 top-4 font-semibold ${
                      plan.destaque
                        ? 'bg-white text-primary hover:bg-white/90'
                        : 'bg-primary text-white hover:bg-primary/90'
                    }`}
                  >
                    {plan.badge_text}
                  </Badge>
                )}
                <CardContent className="p-7">
                  <h3 className={`mb-2 text-base font-semibold ${plan.destaque ? 'text-white' : 'text-card-foreground'}`}>
                    {plan.nome}
                  </h3>
                  <div className="mb-4">
                    <span className={`text-xl font-bold ${plan.destaque ? 'text-white' : 'text-card-foreground'}`}>
                      {plan.preco_centavos === 0 ? 'Grátis' : formatPrice(plan.preco_centavos)}
                    </span>
                    {plan.preco_centavos > 0 && (
                      <span className={`text-base ${plan.destaque ? 'text-white/80' : 'text-muted-foreground'}`}>
                        /{plan.intervalo === 'ANUAL' ? 'ano' : 'mês'}
                      </span>
                    )}
                    {plan.trial_dias != null && plan.trial_dias > 0 && (
                      <span className={`ml-2 text-sm ${plan.destaque ? 'text-white/70' : 'text-muted-foreground'}`}>
                        ({plan.trial_dias} dias grátis)
                      </span>
                    )}
                  </div>
                  {plan.descricao && (
                    <p className={`mb-6 text-sm ${plan.destaque ? 'text-white/80' : 'text-muted-foreground'}`}>
                      {plan.descricao}
                    </p>
                  )}

                  {plan.features_display && plan.features_display.length > 0 && (
                    <div className="mb-8 flex flex-col gap-3">
                      {[...plan.features_display].sort((a, b) => Number(b.included) - Number(a.included)).map((feature, idx) => (
                        <div
                          key={idx}
                          className={`flex items-center gap-2 ${feature.included ? '' : 'opacity-50'}`}
                        >
                          <CheckCircle
                            className={`h-[18px] w-[18px] ${
                              feature.included
                                ? plan.destaque ? 'text-white' : 'text-primary'
                                : 'text-muted-foreground'
                            }`}
                          />
                          <span
                            className={`text-sm ${
                              feature.included ? '' : 'line-through'
                            } ${
                              plan.destaque ? 'text-white' : 'text-card-foreground'
                            }`}
                          >
                            {feature.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  <Button
                    variant={plan.destaque ? 'secondary' : 'outline'}
                    size="lg"
                    className={`w-full py-3 font-semibold ${
                      plan.destaque
                        ? 'bg-white text-primary hover:bg-white/90'
                        : ''
                    }`}
                    onClick={() => login(plan.codigo, undefined, 'signup')}
                  >
                    {plan.cta_text || 'Selecionar'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {oferta && !loading && (
          <div className="mx-auto mt-12 max-w-3xl">
            <SecretarioAddonCard
              oferta={oferta}
              intervalo={interval}
              nota="No teste grátis de 14 dias o Secretário já vem com 5 triagens."
            >
              {!addonDisponivel(oferta, interval) && <Badge variant="outline">Em breve</Badge>}
            </SecretarioAddonCard>
          </div>
        )}
      </div>
    </div>
  );
}

// Demonstração do produto — capturas dos mockups com dados fictícios.
// Imagens em public/demo/ (referenciadas por URL para não depender de tipagem de import).
const SHOWCASE = [
  {
    key: 'painel',
    label: 'Painel',
    img: '/demo/painel.png',
    titulo: 'Tudo o que importa na primeira tela',
    desc: 'Intimações do DJEN com o prazo informado em destaque, seus processos ativos e a agenda — sem caçar em menu nenhum.',
  },
  {
    key: 'autos',
    label: 'Autos digitais',
    img: '/demo/autos.png',
    titulo: 'Os autos integrados, sem sair do sistema',
    desc: 'Documentos e movimentações do processo com visualizador embutido, atualizados a partir da base nacional do CNJ.',
  },
  {
    key: 'peticionar',
    label: 'Peticionamento',
    img: '/demo/peticionar.png',
    titulo: 'Peticione e assine digitalmente',
    desc: 'Monte a petição com anexos, marque o sigilo quando necessário e protocole com assinatura digital e acompanhamento de cada etapa.',
  },
] as const;

function ProductShowcase() {
  const [active, setActive] = useState<(typeof SHOWCASE)[number]['key']>('painel');
  const current = SHOWCASE.find((s) => s.key === active) ?? SHOWCASE[0];

  return (
    <div id="demo" className="scroll-mt-20 bg-background py-16 md:py-24">
      <div className="container mx-auto max-w-screen-lg px-4">
        <div className="mb-10 text-center">
          <h2 className="mb-4 text-3xl font-bold text-foreground">
            Veja a plataforma por dentro
          </h2>
          <p className="mx-auto max-w-[600px] text-base text-muted-foreground">
            Do acompanhamento do processo ao protocolo — tudo num só lugar.
          </p>
        </div>

        <div className="mb-8 flex flex-wrap items-center justify-center gap-2">
          {SHOWCASE.map((s) => (
            <Button
              key={s.key}
              variant={active === s.key ? 'default' : 'ghost'}
              size="sm"
              className="rounded-full"
              onClick={() => setActive(s.key)}
            >
              {s.label}
            </Button>
          ))}
        </div>

        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-lg">
          {/* Barra de janela falsa, para enquadrar a captura como uma "tela" */}
          <div className="flex items-center gap-1.5 border-b border-border bg-muted px-4 py-2.5">
            <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
            <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
            <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
          </div>
          <img
            src={current.img}
            alt={`JurisApp — ${current.label}`}
            className="block w-full"
            loading="lazy"
          />
        </div>

        <div className="mx-auto mt-6 max-w-[640px] text-center">
          <p className="text-base font-semibold text-foreground">{current.titulo}</p>
          <p className="mt-1 text-sm text-muted-foreground">{current.desc}</p>
          <p className="mt-3 text-xs text-muted-foreground">
            Imagem ilustrativa — dados fictícios.
          </p>
        </div>
      </div>
    </div>
  );
}

function LandingPage() {
  const { login, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Logado: "Abrir o app" e "Sair" no lugar de "Entrar" e "Registrar-se".
  const entrar = isAuthenticated
    ? { label: 'Sair', onClick: () => logout() }
    : { label: 'Entrar', onClick: () => login() };
  const principal = isAuthenticated
    ? { label: 'Abrir o app', onClick: () => navigate('/app') }
    : { label: 'Registrar-se', onClick: () => login('free_trial', undefined, 'signup') };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header — landmark <nav> implícito via header */}
      <header className="fixed top-[var(--env-banner-h,0px)] z-50 w-full border-b border-border bg-background/92 backdrop-blur-md">
        <div className="container mx-auto flex h-14 max-w-screen-lg items-center justify-between px-4">
          <LandingPageLogo />

          {/* Desktop Navigation */}
          <div className="hidden items-center gap-8 md:flex">
            {navLinks.map((link) =>
              'to' in link ? (
                <Link
                  key={link.label}
                  to={link.to}
                  className="text-sm font-medium text-muted-foreground no-underline transition-colors hover:text-primary"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-sm font-medium text-muted-foreground no-underline transition-colors hover:text-primary"
                >
                  {link.label}
                </a>
              ),
            )}
            <div className="ml-2 flex items-center gap-2">
              <Button
                variant="outline"
                onClick={entrar.onClick}
                className="border-primary/40 px-4 py-2 text-primary hover:bg-primary/5 hover:text-primary"
              >
                {entrar.label}
              </Button>
              <Button
                variant="unstyled"
                onClick={principal.onClick}
                className="bg-gradient-to-br from-primary to-[var(--color-primary-dark)] px-4 py-2 text-white shadow hover:opacity-90"
              >
                {principal.label}
              </Button>
            </div>
          </div>

          {/* Mobile menu button */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" aria-label="Abrir menu de navegação">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px]">
              <div className="p-4">
                <LandingPageLogo />
              </div>
              <nav className="flex flex-col">
                {navLinks.map((link) =>
                  'to' in link ? (
                    <Link
                      key={link.label}
                      to={link.to}
                      onClick={() => setMobileMenuOpen(false)}
                      className="w-full px-4 py-3 text-left text-sm text-muted-foreground no-underline hover:bg-accent"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      key={link.label}
                      href={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="w-full px-4 py-3 text-left text-sm text-muted-foreground no-underline hover:bg-accent"
                    >
                      {link.label}
                    </a>
                  ),
                )}
                <Button
                  variant="ghost"
                  onClick={entrar.onClick}
                  className="w-full justify-start rounded-none px-4 py-3 text-left text-sm font-normal text-muted-foreground"
                >
                  {entrar.label}
                </Button>
                <Button
                  variant="ghost"
                  onClick={principal.onClick}
                  className="w-full justify-start rounded-none px-4 py-3 text-left text-sm font-semibold text-primary"
                >
                  {principal.label}
                </Button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main id="main-content" tabIndex={-1}>
      {/* Hero Section */}
      <div className="hex-pattern bg-background pb-16 pt-24 md:pb-24 md:pt-32">
        <div className="container mx-auto max-w-screen-lg px-4">
          <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2">
            <div>
              <h1 className="mb-4 text-2xl font-bold tracking-tight sm:text-3xl md:text-5xl">
                Seus processos, prazos e petições{' '}
                <span className="text-primary">num só lugar</span>
              </h1>
              <p className="mb-8 text-base text-muted-foreground">
                Consulta na PDPJ, comunicações do DJEN, peticionamento e assinatura com certificado digital — tudo integrado aos tribunais.
              </p>
              <div className="flex flex-col gap-4 sm:flex-row">
                <Button
                  variant="unstyled"
                  size="lg"
                  onClick={() => login('free_trial', undefined, 'signup')}
                  className="bg-gradient-to-br from-primary to-[var(--color-primary-dark)] px-8 py-3 text-white shadow hover:opacity-90"
                >
                  Descobrir Soluções
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  asChild
                  className="px-8 py-3"
                >
                  <a href="#planos">Ver Planos</a>
                </Button>
              </div>
            </div>
            <div>
              {/* Capability Cards — destaques reais, sem métricas inventadas */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {[
                  { label: 'Consulta processual integrada (PDPJ)', icon: <Scale className="h-5 w-5" /> },
                  { label: 'Assinatura digital com validade jurídica', icon: <ShieldCheck className="h-5 w-5" /> },
                  { label: 'Peticionamento eletrônico', icon: <Send className="h-5 w-5" /> },
                  { label: 'Comunicações do DJEN', icon: <Bell className="h-5 w-5" /> },
                ].map((cap, index) => (
                  <Card
                    key={cap.label}
                    className={`px-4 py-6 text-center ${
                      index % 2 === 0
                        ? 'bg-card border border-border'
                        : 'bg-primary border-none text-white'
                    }`}
                  >
                    <div
                      className={`mb-2 flex justify-center ${
                        index % 2 === 0 ? 'text-primary' : 'text-white'
                      }`}
                    >
                      {cap.icon}
                    </div>
                    <span className={`text-xs font-medium ${index % 2 === 0 ? 'text-card-foreground' : 'text-white'}`}>
                      {cap.label}
                    </span>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="recursos" className="scroll-mt-20 bg-muted py-16 md:py-24">
        <div className="container mx-auto max-w-screen-lg px-4">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-3xl font-bold text-foreground">
              Recursos Completos
            </h2>
            <p className="mx-auto max-w-[600px] text-base text-muted-foreground">
              Tudo que você precisa para gerenciar seu escritório de forma eficiente
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-4">
            {features.map((feature) => (
              <Card
                key={feature.title}
                className={`h-full transition-all hover:-translate-y-0.5 ${
                  feature.variant === 'primary'
                    ? 'border-none bg-primary text-white'
                    : 'border border-border bg-card'
                }`}
              >
                <CardContent className="p-6 text-center">
                  <div
                    className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg ${
                      feature.variant === 'primary'
                        ? 'bg-white/15 text-white'
                        : 'bg-primary/10 text-primary'
                    }`}
                  >
                    {feature.icon}
                  </div>
                  <h3
                    className={`mb-2 text-base font-semibold ${
                      feature.variant === 'primary' ? 'text-white' : 'text-card-foreground'
                    }`}
                  >
                    {feature.title}
                  </h3>
                  <p
                    className={`text-sm ${
                      feature.variant === 'primary' ? 'text-white/80' : 'text-muted-foreground'
                    }`}
                  >
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Additional Features List */}
          <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3">
            {additionalFeatures.map((item) => (
              <div key={item.title} className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  {item.icon}
                </div>
                <div>
                  <p className="mb-1 text-sm font-semibold text-foreground">
                    {item.title}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Roadmap — "Em breve", honesto sobre o que ainda não existe */}
          <div className="mt-16">
            <div className="mb-6 flex items-center justify-center gap-3">
              <span className="h-px w-8 bg-border" />
              <span className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
                <Clock className="h-3.5 w-3.5" /> Em breve
              </span>
              <span className="h-px w-8 bg-border" />
            </div>
            <div className="mx-auto grid max-w-3xl grid-cols-1 gap-8 sm:grid-cols-2">
              {roadmapFeatures.map((item) => (
                <div key={item.title} className="flex items-start gap-4 opacity-70">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-muted-foreground/10 text-muted-foreground">
                    {item.icon}
                  </div>
                  <div>
                    <p className="mb-1 text-sm font-semibold text-foreground">
                      {item.title}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Demonstração — capturas dos mockups (dados fictícios) */}
      <ProductShowcase />

      {/* About Section */}
      <div id="sobre" className="scroll-mt-20 bg-background py-16 md:py-24">
        <div className="container mx-auto max-w-screen-lg px-4">
          <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2">
            <div>
              <h2 className="mb-4 text-3xl font-bold text-foreground">
                Sobre a JurisApp
              </h2>
              <p className="mb-6 text-base font-semibold text-muted-foreground">
                Tecnologia aplicada à rotina jurídica para reduzir tarefas manuais e acelerar resultados.
              </p>
              <p className="mb-8 text-sm text-muted-foreground">
                Nossa plataforma combina gestão processual, documentos e peticionamento em uma única experiência,
                com foco em segurança, produtividade e clareza para equipes jurídicas.
              </p>
              <div className="flex flex-col gap-4 sm:flex-row">
                <Button
                  variant="unstyled"
                  asChild
                  className="bg-gradient-to-br from-primary to-[var(--color-primary-dark)] px-8 py-2 text-white shadow hover:opacity-90"
                >
                  <Link to="/sobre">Conhecer a JurisApp</Link>
                </Button>
                <Button variant="outline" asChild className="px-8 hover:bg-primary/10 hover:text-primary">
                  <Link to="/contato">Falar com consultor</Link>
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[
                { title: 'Segurança', desc: 'Controle de acesso, auditoria e criptografia em repouso e em trânsito.' },
                { title: 'Automação', desc: 'Fluxos inteligentes para petições, anexos e integrações.' },
                { title: 'Visibilidade', desc: 'Dashboards claros para desempenho e prioridades.' },
                { title: 'Suporte humano', desc: 'Time pronto para acompanhar seu crescimento.' },
              ].map((item, index) => (
                <Card
                  key={item.title}
                  className={`h-full border border-border ${index % 2 === 0 ? 'bg-card' : 'bg-primary/10'}`}
                >
                  <CardContent className="p-6">
                    <p className="mb-2 text-base font-semibold text-card-foreground">
                      {item.title}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {item.desc}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <PricingSection login={login} />

      {/* CTA Section — usa gradient primário (identidade visual, não tema) */}
      <div className="bg-gradient-to-br from-primary to-[var(--color-primary-dark)] py-16 text-center md:py-20">
        <div className="container mx-auto max-w-screen-md px-4">
          <h2 className="mb-4 text-2xl font-bold text-white sm:text-3xl">
            Pronto para Começar?
          </h2>
          <p className="mb-8 text-base text-white/90">
            Experimente a plataforma com período de avaliação gratuito. Sem compromisso.
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Button
              variant="unstyled"
              size="lg"
              onClick={() => login('free_trial', undefined, 'signup')}
              className="bg-white px-8 py-3 font-semibold text-primary shadow-md hover:bg-white/90"
            >
              Iniciar Trial Grátis
            </Button>
            <Button
              variant="unstyled"
              size="lg"
              asChild
              className="border border-white/60 bg-transparent px-8 py-3 text-white hover:bg-white/20"
            >
              <Link to="/contato">Falar com Consultor</Link>
            </Button>
          </div>
        </div>
      </div>

      </main>

      <PublicFooter />
    </div>
  );
}

export default LandingPage;
